import React, { useCallback, useEffect, useState } from 'react';
import config from '../config';
import { toast } from 'react-toastify';
import OutreachCallStatus from './OutreachCallStatus';
import {
  formatOutreachHistoryLine,
  outreachForVehicle,
  summarizeOutreach,
} from '../utils/driverOutreachDisplay';
import s from '../pages/bookings/Bookings.module.scss';

const BookingOutreachModal = ({
  bookingId,
  refreshToken = 0,
  onClose,
  onLogCall,
}) => {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [vehicleIdInput, setVehicleIdInput] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driverNameInput, setDriverNameInput] = useState('');
  const [driverSuggestions, setDriverSuggestions] = useState([]);
  const [callingDrivers, setCallingDrivers] = useState({});

  const loadData = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const headers = { Authorization: `Bearer ${token}` };
      const [historyRes, suggestionsRes] = await Promise.all([
        fetch(`${config.dispatchApiBase}/ml/outreach-history/${bookingId}`, { headers }),
        fetch(`${config.dispatchApiBase}/ml/driver-suggestions/${bookingId}`, { headers }),
      ]);
      const historyData = await historyRes.json().catch(() => ({}));
      const suggestionsData = await suggestionsRes.json().catch(() => ({}));
      if (historyRes.ok && historyData.success) {
        setHistory(historyData.history || []);
      } else {
        setHistory([]);
      }
      if (suggestionsRes.ok && suggestionsData.success) {
        setSuggestions(suggestionsData.suggestions || []);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Booking outreach load error:', err);
      toast.error('Failed to load call history');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshToken]);

  useEffect(() => {
    const fetchDrivers = async () => {
      setDriversLoading(true);
      try {
        const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
        const response = await fetch(`${config.opsApiBase}/vehicle-drivers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok && data?.success && Array.isArray(data?.data)) {
          const mapped = data.data
            .map((v) => ({
              vehicle_id: Number(v.vehicle_id),
              driver_name: (v.driver_name || '').trim(),
              registration_no: v.registration_no || '',
              driver_phone: v.driver_phone || '',
            }))
            .filter((v) => Number.isInteger(v.vehicle_id) && v.vehicle_id > 0);
          setDrivers(mapped);
        }
      } catch (err) {
        console.error('Failed to load drivers in outreach modal:', err);
      } finally {
        setDriversLoading(false);
      }
    };
    fetchDrivers();
  }, []);

  const handleDriverNameInputChange = (val) => {
    setDriverNameInput(val);
    if (!val.trim()) {
      setDriverSuggestions([]);
      return;
    }
    const q = val.toLowerCase();
    const matches = drivers
      .filter((d) => {
        const name = (d.driver_name || '').trim().toLowerCase();
        const regNo = (d.registration_no || '').trim().toLowerCase();
        const vid = `v-${d.vehicle_id}`.toLowerCase();
        return (
          name.includes(q) ||
          regNo.includes(q) ||
          vid.includes(q) ||
          String(d.vehicle_id).includes(q)
        );
      })
      .slice(0, 10);
    setDriverSuggestions(matches);
  };

  const selectDriverSuggestion = (d) => {
    setVehicleIdInput(String(d.vehicle_id));
    setDriverNameInput(d.driver_name || '');
    setDriverSuggestions([]);
  };

  const historyForVehicle = (vehicleId) => outreachForVehicle(history, vehicleId);

  const startLogCall = (vehicleId, driverName = '') => {
    const vid = Number(vehicleId);
    if (!Number.isInteger(vid) || vid <= 0) {
      toast.error('Enter a valid vehicle ID');
      return;
    }
    const driverMatch = drivers.find((v) => v.vehicle_id === vid);
    onLogCall(vid, driverMatch?.driver_name || driverName || driverNameInput.trim());
  };

  const handlePlaceCall = async (driverPhone) => {
    if (!driverPhone) return;
    const cleanPhone = String(driverPhone).replace(/\D/g, '');
    if (!cleanPhone) return;

    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      let chosenSim = null;
      try {
        const simRes = await fetch(`${config.opsApiBase}/call-server/active-sims?role=driver`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const simData = await simRes.json();
        const onlineSims = (simData?.success && simData?.sims) ? simData.sims : [];

        if (onlineSims.length === 0) {
          if (!window.confirm(`⚠️ No Driver SIM phone is currently detected online.\nAttempt to dial ${driverPhone} anyway?`)) {
            return;
          }
        } else if (onlineSims.length === 1) {
          if (!window.confirm(`Initiate call to driver ${driverPhone} via Phone 1 (${onlineSims[0].sim_number})?`)) {
            return;
          }
          chosenSim = onlineSims[0].sim_number;
        } else {
          // Multiple SIMs online - let operator pick
          const simListStr = onlineSims.map((s, i) => `${i + 1}: ${s.sim_number}`).join('\n');
          const selection = window.prompt(
            `Multiple Driver Phones Online!\nChoose which phone to dial from:\n${simListStr}\n\nEnter option (1 to ${onlineSims.length}):`,
            "1"
          );
          if (!selection) return;
          const idx = parseInt(selection.trim(), 10) - 1;
          if (idx >= 0 && idx < onlineSims.length) {
            chosenSim = onlineSims[idx].sim_number;
          } else {
            chosenSim = onlineSims[0].sim_number;
          }
        }
      } catch (err) {
        if (!window.confirm(`Initiate call to driver ${driverPhone} via office Call Server?`)) {
          return;
        }
      }

      setCallingDrivers((prev) => ({ ...prev, [cleanPhone]: true }));

      const response = await fetch(`${config.opsApiBase}/call-driver`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          driver_phone: cleanPhone,
          sim_number: chosenSim,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert(`📞 Call initiated successfully to ${driverPhone}!`);
      } else {
        alert(`Failed to place call: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error placing call:', error);
      alert(`Error placing call: ${error.message}`);
    } finally {
      setCallingDrivers((prev) => ({ ...prev, [cleanPhone]: false }));
    }
  };

  return (
    <div className={s.modalOverlay} onClick={onClose}>
      <div
        className={s.modalContent}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '520px' }}
      >
        <div className={s.modalHeader}>
          <i className="fa fa-phone-square" style={{ color: '#2563eb', fontSize: '24px' }} />
          <h3>Driver call log</h3>
        </div>
        <div className={s.modalBody}>
          <p className={s.modalLabel}>Booking</p>
          <p className={s.modalValue}>#{bookingId}</p>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 16px' }}>
            Log calls you made before or after assignment. Use this if you forgot to log rejections
            while assigning.
          </p>

          {loading ? (
            <p style={{ fontSize: '13px', color: '#6b7280' }}>
              <i className="fa fa-spinner fa-spin" style={{ marginRight: '6px' }} />
              Loading...
            </p>
          ) : (
            <>
              {history.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <p className={s.modalLabel}>Logged calls</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {history.map((row) => (
                      <div
                        key={row.id || `${row.vehicle_id}-${row.logged_at}`}
                        style={{
                          fontSize: '13px',
                          padding: '8px 10px',
                          background: '#f3f4f6',
                          borderRadius: '6px',
                        }}
                      >
                        <strong>V-{row.vehicle_id}</strong> — {formatOutreachHistoryLine(row)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {suggestions.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <p className={s.modalLabel}>Suggested drivers</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {suggestions.map((item) => {
                      const vehicleCalls = historyForVehicle(item.vehicle_id);
                      const outreachSummary = summarizeOutreach(vehicleCalls);
                      return (
                        <div
                          key={item.vehicle_id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '8px',
                            border: outreachSummary
                              ? outreachSummary.badgeStyle.border
                              : '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '8px 10px',
                            background: outreachSummary
                              ? outreachSummary.badgeStyle.background
                              : '#fff',
                          }}
                        >
                          <div style={{ fontSize: '13px', flex: 1 }}>
                            <strong>
                              #{item.suggestion_rank} V-{item.vehicle_id}
                              {item.driver_name ? ` · ${item.driver_name}` : ''}
                              {item.is_transporter ? (
                                <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 800, fontSize: '0.78rem' }}>
                                  transporter
                                </span>
                              ) : null}
                            </strong>
                            {item.driver_phone ? (
                              <div style={{ marginTop: '2px' }}>
                                {(() => {
                                  const clean = String(item.driver_phone).replace(/\D/g, '');
                                  const isCalling = callingDrivers[clean];
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => handlePlaceCall(item.driver_phone)}
                                      disabled={isCalling}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: isCalling ? '#f59e0b' : '#2563eb',
                                        cursor: isCalling ? 'wait' : 'pointer',
                                        textDecoration: 'underline',
                                        whiteSpace: 'nowrap',
                                        padding: 0,
                                        fontFamily: 'inherit',
                                        fontSize: '12px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <span>{isCalling ? '📞 Calling...' : `📞 ${item.driver_phone}`}</span>
                                    </button>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                No phone number
                              </div>
                            )}
                            <OutreachCallStatus calls={vehicleCalls} compact />
                          </div>
                          <button
                            type="button"
                            className={s.modalCancelBtn}
                            style={{ padding: '4px 10px', fontSize: '12px', flexShrink: 0 }}
                            onClick={() => startLogCall(item.vehicle_id, item.driver_name)}
                          >
                            {outreachSummary
                              ? `Log again (${outreachSummary.callCount}×)`
                              : 'Log call'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className={s.modalLabel}>Vehicle ID</p>
              <input
                type="number"
                min="1"
                className={s.modalInput}
                value={vehicleIdInput}
                onChange={(e) => setVehicleIdInput(e.target.value)}
                placeholder="Enter vehicle ID"
                style={{ width: '100%', boxSizing: 'border-box', marginBottom: '12px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') startLogCall(vehicleIdInput.trim(), driverNameInput.trim());
                }}
              />

              <p style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', margin: '0 0 12px' }}>— or —</p>

              <p className={s.modalLabel}>Driver name</p>
              <div style={{ position: 'relative', marginBottom: '16px' }}>
                <input
                  type="text"
                  className={s.modalInput}
                  value={driverNameInput}
                  onChange={(e) => handleDriverNameInputChange(e.target.value)}
                  onFocus={() => {
                    if (driverNameInput.trim()) handleDriverNameInputChange(driverNameInput);
                  }}
                  onBlur={() => {
                    window.setTimeout(() => setDriverSuggestions([]), 150);
                  }}
                  placeholder={driversLoading ? 'Loading drivers...' : 'Type to search driver name'}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (driverSuggestions.length > 0) {
                        e.preventDefault();
                        selectDriverSuggestion(driverSuggestions[0]);
                        return;
                      }
                      startLogCall(vehicleIdInput.trim(), driverNameInput.trim());
                    }
                    if (e.key === 'Escape') {
                      setDriverSuggestions([]);
                    }
                  }}
                />
                {driverSuggestions.length > 0 && (
                  <div className={s.addressSuggestions} style={{ position: 'absolute', left: 0, right: 0, zIndex: 10, marginTop: '4px' }}>
                    <div className={s.addressSuggestionsHeader}>
                      <i className="fa fa-user" />
                      Matching drivers:
                    </div>
                    {driverSuggestions.map((d) => (
                      <button
                        key={d.vehicle_id}
                        type="button"
                        className={s.addressSuggestionItem}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectDriverSuggestion(d)}
                      >
                        <strong>{d.driver_name || 'Unnamed driver'}</strong>
                        <small>
                          V-{d.vehicle_id}
                          {d.registration_no ? ` • ${d.registration_no}` : ''}
                        </small>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {(() => {
                const vid = Number.parseInt((vehicleIdInput || '').trim(), 10);
                if (!Number.isInteger(vid) || vid <= 0) return null;
                const driverMatch = drivers.find((v) => v.vehicle_id === vid);
                if (!driverMatch?.driver_phone) return null;

                const clean = String(driverMatch.driver_phone).replace(/\D/g, '');
                const isCalling = callingDrivers[clean];

                return (
                  <button
                    type="button"
                    className={s.modalCancelBtn}
                    onClick={() => handlePlaceCall(driverMatch.driver_phone)}
                    disabled={isCalling}
                    style={{
                      width: '100%',
                      marginBottom: '8px',
                      borderColor: '#10b981',
                      color: '#10b981',
                      fontWeight: 600,
                      padding: '10px 14px',
                      boxSizing: 'border-box',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <i className="fa fa-phone" style={{ marginRight: '6px' }} />
                    {isCalling ? 'Calling...' : `Call Driver (${driverMatch.driver_phone})`}
                  </button>
                );
              })()}

              <button
                type="button"
                className={s.modalConfirmBtn}
                style={{ width: '100%', padding: '10px 14px', boxSizing: 'border-box', justifyContent: 'center' }}
                onClick={() => startLogCall(vehicleIdInput.trim(), driverNameInput.trim())}
              >
                <i className="fa fa-phone" style={{ marginRight: '6px' }} />
                Log call
              </button>
            </>
          )}
        </div>
        <div className={s.modalFooter}>
          <button type="button" className={s.modalCancelBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingOutreachModal;
